import React, { useState } from 'react';
import { closeAndReassign, deleteRepBrick } from '../services/adminService';

const BrickAssignmentPanel = ({
  repBricks, teams, regions, reps,
  tid, isSuperAdmin, mdTenantId,
  loadMasterData, setMessage, styles
}) => {
  const [filterTeamId, setFilterTeamId] = useState('');
  const [filterRegionId, setFilterRegionId] = useState('');
  const [filterRepId, setFilterRepId] = useState('');
  const [closingBrickId, setClosingBrickId] = useState(null);
  const [closingRb, setClosingRb] = useState(null);
  const [closingEndDate, setClosingEndDate] = useState(''); // YYYY-MM-DD (ayın son günü)
  const [closingMonth, setClosingMonth] = useState('');    // YYYY-MM (input value)
  const [newStartDate, setNewStartDate] = useState('');    // YYYY-MM-DD (sonraki ayın 1'i)
  const [assignments, setAssignments] = useState([]);      // yeni atama satırları
  const [saving, setSaving] = useState(false);

  const reload = () => loadMasterData(isSuperAdmin ? mdTenantId : null);

  const visibleRegions = filterTeamId
    ? regions.filter(r => String(r.teamId) === String(filterTeamId))
    : regions;

  const visibleReps = filterRegionId
    ? reps.filter(r => String(r.regionId) === String(filterRegionId))
    : filterTeamId
      ? reps.filter(r => {
          const rg = regions.find(rg => rg.id === r.regionId);
          return String(rg?.teamId) === String(filterTeamId);
        })
      : reps;

  const visibleBricksWithWhite = repBricks.filter(rb => {
    if (!filterTeamId) return false;
    if (String(rb.teamId) !== String(filterTeamId)) return false;
    if (filterRegionId && String(rb.regionId) !== String(filterRegionId)) return false;
    if (filterRepId && String(rb.repId) !== String(filterRepId)) return false;
    return true;
  });

  const totalRatio = assignments.reduce((sum, a) => sum + (parseFloat(a.splitRatio) || 0), 0);

  const handleMonthChange = (monthValue) => {
    setClosingMonth(monthValue);
    if (monthValue) {
      const [year, month] = monthValue.split('-').map(Number);
      const lastDay = new Date(year, month, 0).getDate();
      const endFull = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      const nextMonth = month === 12 ? 1 : month + 1;
      const nextYear = month === 12 ? year + 1 : year;
      const startFull = `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`;
      setClosingEndDate(endFull);
      setNewStartDate(startFull);
    } else {
      setClosingEndDate('');
      setNewStartDate('');
    }
  };

  const addAssignmentRow = () => {
    setAssignments(prev => [...prev, {
      id: Date.now(),
      repId: '',
      splitRatio: 100 - totalRatio > 0 ? 100 - totalRatio : 0,
      whiteFlag: false,
      teamId: closingRb?.teamId || '',
      regionId: closingRb?.regionId || ''
    }]);
  };

  const updateAssignment = (id, field, value) => {
    setAssignments(prev => prev.map(a =>
      a.id === id ? { ...a, [field]: value } : a
    ));
  };

  const removeAssignment = (id) => {
    setAssignments(prev => prev.filter(a => a.id !== id));
  };

  const openClosingForm = (rb) => {
    setClosingBrickId(rb.id);
    setClosingRb(rb);
    setClosingMonth('');
    setClosingEndDate('');
    setNewStartDate('');
    setAssignments([{
      id: Date.now(),
      repId: '',
      splitRatio: rb.splitRatio,
      whiteFlag: false,
      teamId: rb.teamId || '',
      regionId: rb.regionId || ''
    }]);
  };

  const cancelClosing = () => {
    setClosingBrickId(null);
    setClosingRb(null);
    setClosingMonth('');
    setClosingEndDate('');
    setNewStartDate('');
    setAssignments([]);
  };

  const handleCloseAndReassign = async () => {
    if (!closingEndDate) { alert('Bitiş ayı seçin.'); return; }
    if (!newStartDate) { alert('Başlangıç tarihi hesaplanamadı.'); return; }
    if (assignments.length === 0) { alert('En az bir atama satırı ekleyin.'); return; }
    if (totalRatio !== 100) { alert(`Toplam oran ${totalRatio}% — 100% olmalı.`); return; }

    for (const a of assignments) {
      if (!a.whiteFlag && !a.repId) { alert('Tüm satırlarda mümessil seçin veya White Flag işaretleyin.'); return; }
      if (a.whiteFlag && !a.regionId) { alert('White Flag satırlarında bölge seçin.'); return; }
    }

    setSaving(true);
    try {
      await closeAndReassign(closingRb.id, {
        endDate: closingEndDate,
        brickId: closingRb.brickId,
        newStartDate: newStartDate,
        assignments: assignments.map(a => ({
          repId: a.whiteFlag ? null : parseInt(a.repId),
          splitRatio: parseFloat(a.splitRatio),
          whiteFlag: a.whiteFlag,
          teamId: a.whiteFlag ? parseInt(a.teamId) : (() => {
            const rep = reps.find(r => r.id === parseInt(a.repId));
            const region = regions.find(r => r.id === rep?.regionId);
            return region?.teamId || null;
          })(),
          regionId: a.whiteFlag ? parseInt(a.regionId) : (() => {
            const rep = reps.find(r => r.id === parseInt(a.repId));
            return rep?.regionId || null;
          })()
        }))
      }, tid);
      cancelClosing();
      setMessage('Atama kapatıldı ve yeni atamalar oluşturuldu.');
      reload();
    } catch (err) {
      setMessage(err?.response?.data?.message || 'İşlem başarısız.');
    }
    finally { setSaving(false); }
  };

  const regionsByTeam = (teamId) => regions.filter(r => String(r.teamId) === String(teamId));

  const currentYear = new Date().getFullYear();
  const minMonth = closingRb?.startDate?.substring(0, 7);
  const maxMonth = `${currentYear + 1}-12`;

  return (
    <div style={styles.card}>
      <h3 style={styles.cardTitle}>📍 Brick Atamaları</h3>

      {/* Filtreler */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={styles.label}>Ekip *</label>
          <select
            style={styles.input}
            value={filterTeamId}
            onChange={e => { setFilterTeamId(e.target.value); setFilterRegionId(''); setFilterRepId(''); }}
          >
            <option value="">Ekip seçin...</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.teamName}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={styles.label}>Bölge</label>
          <select
            style={{ ...styles.input, opacity: filterTeamId ? 1 : 0.5 }}
            value={filterRegionId}
            onChange={e => { setFilterRegionId(e.target.value); setFilterRepId(''); }}
            disabled={!filterTeamId}
          >
            <option value="">Tüm bölgeler</option>
            {visibleRegions.map(r => <option key={r.id} value={r.id}>{r.regionName}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={styles.label}>Mümessil</label>
          <select
            style={{ ...styles.input, opacity: filterTeamId ? 1 : 0.5 }}
            value={filterRepId}
            onChange={e => setFilterRepId(e.target.value)}
            disabled={!filterTeamId}
          >
            <option value="">Tüm mümessiller</option>
            {visibleReps.map(r => <option key={r.id} value={r.id}>{r.repName}</option>)}
          </select>
        </div>

        {filterTeamId && (
          <span style={{ fontSize: '13px', color: '#64748b', paddingBottom: '9px' }}>
            {visibleBricksWithWhite.length} kayıt
          </span>
        )}
      </div>

      {!filterTeamId ? (
        <p style={{ color: '#94a3b8', textAlign: 'center', padding: '32px' }}>
          Brick atamalarını görmek için ekip seçin.
        </p>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Brick</th>
                  <th style={styles.th}>Ekip</th>
                  <th style={styles.th}>Bölge</th>
                  <th style={styles.th}>Mümessil</th>
                  <th style={styles.th}>Oran %</th>
                  <th style={styles.th}>Başlangıç</th>
                  <th style={styles.th}>Bitiş</th>
                  <th style={styles.th}>White</th>
                  <th style={styles.th}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {visibleBricksWithWhite.map(rb => {
                  const teamObj = teams.find(t => t.id === rb.teamId);
                  const regionObj = regions.find(r => r.id === rb.regionId);
                  const isActive = !rb.endDate;
                  const isClosing = closingBrickId === rb.id;

                  return (
                    <tr key={rb.id} style={{ backgroundColor: isClosing ? '#fefce8' : 'transparent' }}>
                      <td style={styles.td}><strong>{rb.brickName}</strong></td>
                      <td style={styles.td}>
                        <span style={styles.tenantBadge}>{teamObj?.teamName || '-'}</span>
                      </td>
                      <td style={styles.td}>{regionObj?.regionName || '-'}</td>
                      <td style={styles.td}>{rb.repName || <span style={{ color: '#94a3b8' }}>-</span>}</td>
                      <td style={styles.td}><span style={styles.badge}>{rb.splitRatio}%</span></td>
                      <td style={styles.td}>{rb.startDate}</td>
                      <td style={styles.td}>
                        {rb.endDate
                          ? <span style={{ color: '#ef4444' }}>{rb.endDate}</span>
                          : <span style={{ color: '#22c55e' }}>Aktif</span>}
                      </td>
                      <td style={styles.td}>
                        {rb.whiteFlag ? <span style={{ color: '#f59e0b' }}>⚑</span> : '-'}
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {isActive && !isClosing && (
                            <button
                              style={{ padding: '4px 10px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                              onClick={() => openClosingForm(rb)}
                            >
                              Kapat
                            </button>
                          )}
                          {isClosing && (
                            <button style={styles.dangerButton} onClick={cancelClosing}>
                              İptal
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Kapatma + Yeni atama formu */}
          {closingRb && (
            <div style={{
              marginTop: '16px', padding: '20px',
              backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px'
            }}>
              <h4 style={{ margin: '0 0 4px 0', color: '#166534', fontSize: '15px' }}>
                🔄 Yeniden Atama — <strong>{closingRb.brickName}</strong>
              </h4>
              <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 16px 0' }}>
                Bitiş ayını seçin, yeni atama satırlarını ekleyin. Toplam oran %100 olmalı. İkisi birlikte kaydedilecek.
              </p>

              {/* Bitiş ayı */}
              <div style={{ display: 'flex', gap: '24px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ ...styles.label, color: '#dc2626', fontWeight: '600' }}>
                    Mevcut Atama Bitiş Ayı *
                  </label>
                  <input
                    type="month"
                    value={closingMonth}
                    min={minMonth}
                    max={maxMonth}
                    onChange={e => handleMonthChange(e.target.value)}
                    style={{ ...styles.input, minWidth: '180px', borderColor: '#dc2626' }}
                  />
                  {closingEndDate && (
                    <span style={{ fontSize: '11px', color: '#dc2626' }}>
                      Son gün: {closingEndDate}
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ ...styles.label, color: '#166534', fontWeight: '600' }}>
                    Yeni Atama Başlangıcı
                  </label>
                  <div style={{
                    ...styles.input, minWidth: '180px',
                    backgroundColor: '#e0f2fe', borderColor: '#166534',
                    color: '#166534', fontWeight: '600',
                    display: 'flex', alignItems: 'center'  // içerik dikey ortala
                  }}>
                    {newStartDate
                      ? new Date(newStartDate + 'T00:00:00').toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })
                      : '— ay seçilince otomatik —'}
                  </div>
                </div>
              </div>

              {/* Atama satırları */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ ...styles.label, fontWeight: '600', fontSize: '13px' }}>
                    Yeni Atama Satırları
                    <span style={{
                      marginLeft: '8px',
                      color: totalRatio === 100 ? '#166534' : '#dc2626',
                      fontWeight: '700'
                    }}>
                      (Toplam: %{totalRatio})
                    </span>
                  </label>
                  <button
                    style={{ ...styles.primaryButton, fontSize: '12px', padding: '4px 10px' }}
                    onClick={addAssignmentRow}
                  >
                    + Satır Ekle
                  </button>
                </div>

                {assignments.map((a, idx) => (
                  <div key={a.id} style={{
                    display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end',
                    padding: '10px', marginBottom: '8px',
                    backgroundColor: 'white', border: '1px solid #d1fae5', borderRadius: '6px'
                  }}>
                    <span style={{ fontSize: '12px', color: '#64748b', alignSelf: 'center', minWidth: '20px' }}>
                      {idx + 1}.
                    </span>

                    {/* Oran */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <label style={styles.label}>Oran %</label>
                      <input
                        type="number" min="1" max="100"
                        value={a.splitRatio}
                        onChange={e => updateAssignment(a.id, 'splitRatio', parseFloat(e.target.value) || 0)}
                        style={{ ...styles.input, minWidth: '70px', padding: '6px 8px' }}
                      />
                    </div>

                    {/* White Flag */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <label style={styles.label}>White Flag</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '36px' }}>
                        <input
                          type="checkbox"
                          checked={a.whiteFlag}
                          onChange={e => updateAssignment(a.id, 'whiteFlag', e.target.checked)}
                        />
                        <span style={{ fontSize: '13px' }}>Boş</span>
                      </div>
                    </div>

                    {/* White Flag → Bölge seç | Normal → Mümessil seç */}
                    {a.whiteFlag ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label style={styles.label}>Bölge *</label>
                        <select
                          style={{ ...styles.input, minWidth: '180px', padding: '6px 8px' }}
                          value={a.regionId}
                          onChange={e => updateAssignment(a.id, 'regionId', e.target.value)}
                        >
                          <option value="">Bölge seçin...</option>
                          {regionsByTeam(closingRb.teamId).map(r => (
                            <option key={r.id} value={r.id}>{r.regionName}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label style={styles.label}>Mümessil *</label>
                        <select
                          style={{ ...styles.input, minWidth: '240px', padding: '6px 8px' }}
                          value={a.repId}
                          onChange={e => updateAssignment(a.id, 'repId', e.target.value)}
                        >
                          <option value="">Mümessil seçin...</option>
                          {reps
                            .filter(r => {
                              const rg = regions.find(rg => rg.id === r.regionId);
                              return String(rg?.teamId) === String(closingRb.teamId);
                            })
                            .map(r => {
                              const rg = regions.find(rg => rg.id === r.regionId);
                              return (
                                <option key={r.id} value={r.id}>
                                  {rg?.regionName} → {r.repName}
                                </option>
                              );
                            })}
                        </select>
                      </div>
                    )}

                    {/* Satır sil */}
                    {assignments.length > 1 && (
                      <button
                        style={{ ...styles.dangerButton, alignSelf: 'flex-end' }}
                        onClick={() => removeAssignment(a.id)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Kaydet / İptal */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button
                  style={{
                    ...styles.primaryButton,
                    opacity: (saving || totalRatio !== 100) ? 0.6 : 1
                  }}
                  disabled={saving || totalRatio !== 100}
                  onClick={handleCloseAndReassign}
                >
                  {saving ? 'Kaydediliyor...' : `💾 Kaydet (Toplam: %${totalRatio})`}
                </button>
                <button style={styles.dangerButton} onClick={cancelClosing}>
                  İptal
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BrickAssignmentPanel;